<template>
 <div class="doc-card" :class="{ 'doc-card--empty': !url }">
  <input ref="fileInput" type="file" class="file-input" :accept="accept" @change="onFileChange" />

  <div class="doc-card__preview" @click="url ? $emit('preview', url) : openPicker()">
   <template v-if="url">
    <img v-if="!pdf" :src="url" :alt="title" />
    <div v-else class="pdf-placeholder">
     <i class="far fa-file-pdf"></i>
     <span>PDF document</span>
    </div>
    <span v-if="pdf" class="pdf-badge">PDF</span>
   </template>
   <div v-else class="upload-placeholder">
    <i class="fas fa-cloud-upload-alt"></i>
    <span>Click to upload</span>
   </div>

   <div v-if="uploading" class="uploading-overlay">
    <v-progress-circular indeterminate size="32" width="3" color="#1976d2"></v-progress-circular>
    <span>Uploading&hellip;</span>
   </div>
  </div>

  <div class="doc-card__title">{{ title }}</div>

  <div class="doc-card__footer">
   <span v-if="statusInfo" class="status-pill" :class="`status-pill--${statusInfo.tone}`">
    <i class="fas" :class="statusInfo.icon"></i> {{ statusInfo.text }}
   </span>
   <span v-else class="status-pill status-pill--grey">{{ url ? "Uploaded" : "Not uploaded" }}</span>

   <div class="doc-card__actions">
    <template v-if="url">
     <button type="button" class="action-btn action-btn--outline" @click="$emit('preview', url)">
      <i class="far" :class="pdf ? 'fa-file-alt' : 'fa-eye'"></i> {{ pdf ? "View PDF" : "Preview" }}
     </button>
     <button type="button" class="action-btn action-btn--soft" :disabled="uploading" @click="openPicker"><i class="fas fa-pen"></i> Edit</button>
    </template>
    <button v-else type="button" class="action-btn action-btn--soft" :disabled="uploading" @click="openPicker"><i class="fas fa-upload"></i> Upload</button>
   </div>
  </div>
 </div>
</template>

<script>
// verified codes used by the driver and vehicle document APIs
const STATUS = {
 0: { text: "Rejected", tone: "red", icon: "fa-times-circle" },
 1: { text: "Verified", tone: "green", icon: "fa-check-circle" },
 2: { text: "Pending", tone: "amber", icon: "fa-clock" },
 3: { text: "Expiring soon", tone: "amber", icon: "fa-exclamation-triangle" },
 4: { text: "Expired", tone: "red", icon: "fa-exclamation-circle" },
};

export default {
 name: "DocumentCard",
 props: {
  title: { type: String, required: true },
  url: { type: String, default: "" },
  status: { type: Number, default: null },
  accept: { type: String, default: "image/*,application/pdf" },
  uploading: { type: Boolean, default: false },
 },
 computed: {
  pdf() {
   return !!this.url && this.url.toLowerCase().includes(".pdf");
  },
  statusInfo() {
   return this.url && this.status !== null && this.status !== undefined ? STATUS[this.status] || null : null;
  },
 },
 methods: {
  openPicker() {
   if (!this.uploading) this.$refs.fileInput.click();
  },
  onFileChange(event) {
   const file = event.target.files && event.target.files[0];
   if (file) this.$emit("upload", file);
   // allow picking the same file again later
   event.target.value = "";
  },
 },
};
</script>

<style lang="scss" scoped>
$border: #e5e9f0;
$text: #1f2937;
$muted: #6b7280;

.doc-card {
 display: flex;
 flex-direction: column;
 padding: 14px;
 background: #fff;
 border: 1px solid $border;
 border-radius: 10px;
 transition: box-shadow 0.2s;

 &:hover {
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.08);
 }
}

.file-input {
 display: none;
}

.doc-card__preview {
 position: relative;
 height: 190px;
 border: 1px solid $border;
 border-radius: 6px;
 overflow: hidden;
 background: #f5f7fa;
 cursor: pointer;

 img {
  width: 100%;
  height: 100%;
  object-fit: cover;
 }
}

.pdf-placeholder,
.upload-placeholder {
 height: 100%;
 display: flex;
 flex-direction: column;
 align-items: center;
 justify-content: center;
 gap: 8px;
 font-size: 13px;
 font-weight: 600;
}

.pdf-placeholder {
 color: #6b7280;

 .far {
  font-size: 46px;
  color: #d64545;
 }
}

.pdf-badge {
 position: absolute;
 top: 10px;
 right: 10px;
 padding: 3px 7px;
 font-size: 11px;
 font-weight: 700;
 color: #fff;
 background: #e53935;
 border-radius: 4px;
}

.doc-card--empty .doc-card__preview {
 border: 2px dashed #cfd6e1;
 background: #fafbfc;

 &:hover {
  border-color: #1976d2;
  background: #f3f7fd;
 }
}

.upload-placeholder {
 color: $muted;

 .fas {
  font-size: 34px;
  color: #94a3b8;
 }
}

.uploading-overlay {
 position: absolute;
 inset: 0;
 display: flex;
 flex-direction: column;
 align-items: center;
 justify-content: center;
 gap: 8px;
 background: rgba(255, 255, 255, 0.85);
 font-size: 13px;
 font-weight: 600;
 color: #374151;
}

.doc-card__title {
 margin: 12px 0 10px;
 font-size: 16px;
 font-weight: 700;
 color: $text;
}

.doc-card__footer {
 display: flex;
 align-items: center;
 justify-content: space-between;
 gap: 8px;
 margin-top: auto;
}

.status-pill {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 padding: 4px 12px;
 font-size: 13px;
 font-weight: 600;
 border-radius: 20px;
 white-space: nowrap;
}
.status-pill--green {
 color: #1e7b34;
 background: #e6f4ea;
}
.status-pill--amber {
 color: #b45309;
 background: #fff4e0;
}
.status-pill--red {
 color: #c62828;
 background: #fde8e8;
}
.status-pill--grey {
 color: #4b5563;
 background: #eef0f3;
}

.doc-card__actions {
 display: flex;
 gap: 8px;
}

.action-btn {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 height: 36px;
 padding: 0 14px;
 font-size: 13px;
 font-weight: 600;
 border-radius: 6px;
 white-space: nowrap;
 transition: background-color 0.15s;

 &:disabled {
  opacity: 0.6;
  cursor: not-allowed;
 }
}
.action-btn--outline {
 color: #374151;
 background: #fff;
 border: 1px solid #cfd6e1;

 &:hover {
  background: #f5f7fa;
 }
}
.action-btn--soft {
 color: #1565a8;
 background: #e3f2fb;

 &:hover:not(:disabled) {
  background: #cfe8f7;
 }
}
</style>
